import { httpClient } from '@/core/api/client/httpClient'
import { isHttpError } from '@/core/api/types/http.types'
import type {
  CompletePasswordResetPayload,
  PasswordResetResult,
  StartPasswordResetData,
  StartPasswordResetPayload,
  VerifyPasswordResetPayload,
} from './passwordReset.types'

interface BffApiResponse<T> {
  success: boolean
  data: T | null
  error?: { code: string; message: string } | null
}

function toResult<T>(response: BffApiResponse<T>): PasswordResetResult<T> {
  if (response.success && response.data !== null && response.data !== undefined) {
    return { success: true, data: response.data }
  }
  return {
    success: false,
    errorCode: response.error?.code ?? 'INTERNAL_ERROR',
    message: response.error?.message ?? 'Error inesperado',
  }
}

function toErrorResult<T>(error: unknown): PasswordResetResult<T> {
  if (isHttpError(error) && error.data) {
    const body = error.data as BffApiResponse<T>
    return {
      success: false,
      errorCode: body.error?.code ?? 'INTERNAL_ERROR',
      message: body.error?.message ?? 'Error inesperado',
    }
  }
  return { success: false, errorCode: 'NETWORK_ERROR', message: 'Sin conexión' }
}

export const passwordResetService = {
  async start(
    payload: StartPasswordResetPayload,
  ): Promise<PasswordResetResult<StartPasswordResetData>> {
    try {
      const data = await httpClient.post<BffApiResponse<StartPasswordResetData>>(
        `/api/password-reset/start?lang=${payload.lang ?? 'es'}`,
        { email: payload.email },
      )
      return toResult(data)
    } catch (error) {
      return toErrorResult(error)
    }
  },

  async verify(
    correlationId: string,
    payload: VerifyPasswordResetPayload,
  ): Promise<PasswordResetResult<null>> {
    try {
      const data = await httpClient.post<BffApiResponse<null>>(
        `/api/password-reset/${correlationId}/verify`,
        payload,
      )
      if (data.success) return { success: true, data: null }
      return {
        success: false,
        errorCode: data.error?.code ?? 'INTERNAL_ERROR',
        message: data.error?.message ?? 'Error inesperado',
      }
    } catch (error) {
      return toErrorResult(error)
    }
  },

  async complete(
    correlationId: string,
    payload: CompletePasswordResetPayload,
  ): Promise<PasswordResetResult<null>> {
    try {
      const data = await httpClient.post<BffApiResponse<null>>(
        `/api/password-reset/${correlationId}/complete`,
        payload,
      )
      if (data.success) return { success: true, data: null }
      return {
        success: false,
        errorCode: data.error?.code ?? 'INTERNAL_ERROR',
        message: data.error?.message ?? 'Error inesperado',
      }
    } catch (error) {
      return toErrorResult(error)
    }
  },
}
